import { RabEntry, UfsaMitgeltendeUnterlage } from '../../types';
import { DOKUMENTE, findeDokument, normalisiere, schlageDokumenteVor } from '../../services/mitgeltendeDokumente';
import type { MitgeltendesDokument } from '../../services/mitgeltendeDokumente';
import type { UnterlagenStatus } from '../planungsphase/types';

/**
 * Mitgeltende Unterlagen der Unterlage für spätere Arbeiten (RAB 32).
 *
 * Die Liste nennt Unterlagen, die beim Betreiber liegen und für spätere
 * Arbeiten gelten (Statik, Revisionspläne, Prüfbücher …) — mit Ablageort.
 * Vorschläge kommen aus dem gemeinsamen Katalog der mitgeltenden Dokumente,
 * aber NUR Dokumente mit `ufsa: true`: was nur während der Bauzeit gilt
 * (Abbruchanweisung, Erlaubnisschein …), hat in der Unterlage nichts verloren.
 *
 * Alles hier ist rein: neue Listen, nie die alte verändert — der Autospeicher
 * der Ansicht vergleicht über die Referenz.
 */

export interface UfsaUnterlagenVorschlag {
    dokument: MitgeltendesDokument;
    /** Bauteile des Maßnahmenkatalogs, die den Vorschlag ausgelöst haben (ohne Dubletten). */
    anlaesse: string[];
    /** Bedingungen der auslösenden Regeln — nur Hinweis in der Oberfläche. */
    bedingungen: string[];
}

export interface UfsaUnterlagenVorschlaege {
    /** Passend zu den Einträgen des Maßnahmenkatalogs. */
    passend: UfsaUnterlagenVorschlag[];
    /** Alle übrigen Katalogdokumente mit `ufsa: true` („weitere übliche Unterlagen"). */
    weitere: MitgeltendesDokument[];
}

/** Gespeicherte Liste; fehlt das Feld (ältere Projekte, alte Versionen), ist sie leer. */
export const mitgeltendeUnterlagenListe = (
    liste: UfsaMitgeltendeUnterlage[] | undefined | null,
): UfsaMitgeltendeUnterlage[] => (Array.isArray(liste) ? liste : []);

/**
 * Steht das Katalogdokument schon in der Liste? Über die Katalog-Id, über
 * den Namen (normalisiert) oder über einen Alias (`findeDokument`) — ein
 * frei getippter Eintrag „Statik" zählt so als dasselbe Dokument.
 */
export const istGelistet = (dokument: MitgeltendesDokument, liste: UfsaMitgeltendeUnterlage[]): boolean => {
    const name = normalisiere(dokument.name);
    return liste.some(u =>
        u.katalogId === dokument.id
        || normalisiere(u.name) === name
        || findeDokument(u.name)?.id === dokument.id,
    );
};

const katalogIndex = (id: string): number => {
    const i = DOKUMENTE.findIndex(d => d.id === id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

/**
 * Vorschläge für die Liste. Jeder Eintrag des Maßnahmenkatalogs wird wie ein
 * Arbeitsschritt gefragt (Bauteil + Tätigkeit als Bezeichnung, Kategorie als
 * Gewerk); übrig bleiben nur `ufsa`-Dokumente, die noch nicht gelistet sind.
 * Reihenfolge: Katalogreihenfolge, unabhängig von der Reihenfolge der Einträge.
 */
export const schlageUnterlagenVor = (
    entries: RabEntry[],
    gespeichert: UfsaMitgeltendeUnterlage[] | undefined | null,
): UfsaUnterlagenVorschlaege => {
    const liste = mitgeltendeUnterlagenListe(gespeichert);
    const vorhanden = liste.map(u => u.name).filter(Boolean);
    const treffer = new Map<string, UfsaUnterlagenVorschlag>();

    for (const entry of entries || []) {
        const bezeichnung = [entry.component, entry.activity].filter(t => (t || '').trim()).join(' ');
        if (!bezeichnung) continue;
        const vorschlaege = schlageDokumenteVor({ bezeichnung, gewerk: entry.category, vorhanden });
        for (const v of vorschlaege) {
            if (!v.dokument.ufsa || istGelistet(v.dokument, liste)) continue;
            const bisher = treffer.get(v.dokument.id) || { dokument: v.dokument, anlaesse: [], bedingungen: [] };
            const anlass = (entry.component || '').trim() || bezeichnung;
            if (!bisher.anlaesse.includes(anlass)) bisher.anlaesse.push(anlass);
            for (const b of v.bedingungen || []) {
                if (b && !bisher.bedingungen.includes(b)) bisher.bedingungen.push(b);
            }
            treffer.set(v.dokument.id, bisher);
        }
    }

    const passend = [...treffer.values()].sort((a, b) => katalogIndex(a.dokument.id) - katalogIndex(b.dokument.id));
    const weitere = DOKUMENTE.filter(d => d.ufsa && !treffer.has(d.id) && !istGelistet(d, liste));
    return { passend, weitere };
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLÄGE AUS DER PLANUNGSPHASE (Unterlagen-Checkliste, Status `liegt_vor`)
// ═══════════════════════════════════════════════════════════════════════════

/** Was die Unterlage von einer Zeile der Checkliste braucht (Teilmenge von `SteckbriefUnterlage`). */
export interface PlanungUnterlageKurz {
    id: string;
    name: string;
    status: UnterlagenStatus;
    /** Beleg in der Projektablage (`project_documents.id`). */
    dokument_id: string | null;
    /** Bleibt für spätere Arbeiten von Bedeutung (RAB 32). */
    ufsa: boolean;
}

export interface UfsaPlanungUnterlagenVorschlag {
    /** Id der Checklistenzeile — nur als React-Schlüssel. */
    id: string;
    /** Katalogname, wenn der Katalog die Zeile kennt, sonst der Name der Zeile. */
    name: string;
    dokument: MitgeltendesDokument | null;
    /** In der Planungsphase liegt ein Beleg in der Projektablage. */
    beleg: boolean;
}

/**
 * Unterlagen, die die Planungsphase als vorliegend führt und die für spätere
 * Arbeiten gelten (`ufsa`), als Vorschlag für die Liste — nur solche, die
 * noch nicht gelistet sind. Reihenfolge: Katalog, dann Name. Was NICHT
 * vorliegt (offen, angefordert) wird nicht vorgeschlagen: die Unterlage nennt
 * dem Betreiber, was er hat, nicht, was er haben sollte.
 */
export const unterlagenAusPlanung = (
    unterlagen: readonly PlanungUnterlageKurz[] | null | undefined,
    gespeichert: UfsaMitgeltendeUnterlage[] | undefined | null,
): UfsaPlanungUnterlagenVorschlag[] => {
    const liste = mitgeltendeUnterlagenListe(gespeichert);
    const aus: UfsaPlanungUnterlagenVorschlag[] = [];
    for (const u of unterlagen || []) {
        if (!u.ufsa || u.status !== 'liegt_vor') continue;
        const dokument = findeDokument(u.id) ?? findeDokument(u.name) ?? null;
        const name = dokument?.name ?? (u.name || '').trim();
        if (!name) continue;
        const gelistet = dokument
            ? istGelistet(dokument, liste)
            : liste.some(x => normalisiere(x.name) === normalisiere(name));
        if (gelistet) continue;
        // Dieselbe Unterlage zweimal in der Checkliste (Katalog + eigener Eintrag) → ein Vorschlag.
        const doppelt = aus.some(a => (dokument && a.dokument?.id === dokument.id) || normalisiere(a.name) === normalisiere(name));
        if (doppelt) continue;
        aus.push({ id: u.id, name, dokument, beleg: !!u.dokument_id });
    }
    return aus.sort((a, b) => {
        const ka = a.dokument ? katalogIndex(a.dokument.id) : Number.MAX_SAFE_INTEGER;
        const kb = b.dokument ? katalogIndex(b.dokument.id) : Number.MAX_SAFE_INTEGER;
        return ka !== kb ? ka - kb : a.name.localeCompare(b.name, 'de');
    });
};

/** Vorschlag aus der Planungsphase mit einem Klick übernehmen — Katalogdokument mit Katalog-Id, sonst als freier Eintrag. */
export const uebernehmePlanungUnterlage = (
    liste: UfsaMitgeltendeUnterlage[],
    vorschlag: UfsaPlanungUnterlagenVorschlag,
    id: string,
): UfsaMitgeltendeUnterlage[] =>
    vorschlag.dokument ? uebernehmeDokument(liste, vorschlag.dokument, id) : fuegeUnterlageHinzu(liste, vorschlag.name, id);

/** Katalogdokument mit einem Klick übernehmen (unter seinem Namen, mit Katalog-Id). */
export const uebernehmeDokument = (
    liste: UfsaMitgeltendeUnterlage[],
    dokument: MitgeltendesDokument,
    id: string,
): UfsaMitgeltendeUnterlage[] => {
    if (istGelistet(dokument, liste)) return liste;
    return [...liste, { id, name: dokument.name, katalogId: dokument.id, ablageort: '', bemerkung: '' }];
};

/**
 * Freier Eintrag. Leer oder schon gelistet (gleicher Name, oder dasselbe
 * Katalogdokument unter Alias) → unverändert dieselbe Liste. Erkennt der
 * Katalog den Namen, wird die Katalog-Id mitgeschrieben; der Name bleibt so,
 * wie er getippt wurde.
 */
export const fuegeUnterlageHinzu = (
    liste: UfsaMitgeltendeUnterlage[],
    name: string,
    id: string,
): UfsaMitgeltendeUnterlage[] => {
    const sauber = (name || '').trim();
    if (!sauber) return liste;
    const dokument = findeDokument(sauber);
    if (dokument && istGelistet(dokument, liste)) return liste;
    if (liste.some(u => normalisiere(u.name) === normalisiere(sauber))) return liste;
    return [...liste, { id, name: sauber, katalogId: dokument?.id ?? null, ablageort: '', bemerkung: '' }];
};

export const aktualisiereUnterlage = (
    liste: UfsaMitgeltendeUnterlage[],
    id: string,
    aenderung: Partial<Pick<UfsaMitgeltendeUnterlage, 'name' | 'ablageort' | 'bemerkung'>>,
): UfsaMitgeltendeUnterlage[] => liste.map(u => (u.id === id ? { ...u, ...aenderung } : u));

export const entferneUnterlage = (liste: UfsaMitgeltendeUnterlage[], id: string): UfsaMitgeltendeUnterlage[] =>
    liste.filter(u => u.id !== id);

/** Eine Position nach oben/unten; am Rand dieselbe Referenz zurück. */
export const verschiebeUnterlage = (
    liste: UfsaMitgeltendeUnterlage[],
    id: string,
    richtung: 'up' | 'down',
): UfsaMitgeltendeUnterlage[] => {
    const i = liste.findIndex(u => u.id === id);
    const j = richtung === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= liste.length) return liste;
    const next = [...liste];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
};
